import React, { useMemo } from 'react';
import { List, Text, Icon, IconButton, Cluster } from '@iqueue/iq-ui';
import { agentInfo } from '../../lib/agentRegistry';
import { taskStateMeta, formatElapsed } from '../../lib/formatters';

/** Task tree for the current room: parent/child, states, cancel/retry. */
export default function TasksPanel({ tasks, onCancel, onRetry }) {
  const ordered = useMemo(() => {
    const byId = new Map((tasks || []).map(t => [t.id, t]));
    const childrenOf = new Map();
    const roots = [];
    for (const t of tasks || []) {
      if (t.parentTaskId && byId.has(t.parentTaskId)) {
        if (!childrenOf.has(t.parentTaskId)) childrenOf.set(t.parentTaskId, []);
        childrenOf.get(t.parentTaskId).push(t);
      } else roots.push(t);
    }
    const out = [];
    const walk = (t, depth) => {
      out.push({ task: t, depth });
      for (const c of childrenOf.get(t.id) || []) walk(c, depth + 1);
    };
    for (const r of roots) walk(r, 0);
    return out;
  }, [tasks]);

  if (ordered.length === 0) return <Text variant="body-small" muted>Пока нет задач</Text>;

  return (
    <List aria-label="Задачи комнаты">
      {ordered.map(({ task: t, depth }) => {
        const meta = taskStateMeta(t.state);
        const info = agentInfo(t.assigneeId);
        const cancellable = ['queued', 'running', 'waiting_for_human', 'waiting_for_approval', 'waiting_for_subtasks'].includes(t.state);
        const retryable = ['failed', 'cancelled', 'timed_out'].includes(t.state);
        return (
          <List.Item
            key={t.id}
            leading={<Icon name={meta.icon} />}
            overline={depth > 0 ? (
              <Cluster gap={50} align="center" wrap={false}>
                <Icon name="subdirectory_arrow_right" size="s" />
                подзадача (уровень {depth})
              </Cluster>
            ) : undefined}
            supporting={`${meta.label}${t.waitReason ? ` · ${t.waitReason}` : ''} · ${formatElapsed(t.createdAt)} · @${info.id}`}
            headlineOverflow="truncate"
            trailingAction={(
              <>
                {cancellable && <IconButton icon="close" size="xs" tone="error" label="Отменить" onClick={() => onCancel(t.id)} />}
                {retryable && <IconButton icon="replay" size="xs" label="Повторить" onClick={() => onRetry(t.id)} />}
              </>
            )}
          >
            {t.title || t.instruction || t.id}
          </List.Item>
        );
      })}
    </List>
  );
}
